<template>
  <div :class="{'invalid': showError}" class="standard-email-input">
    <span v-if="showToLabel" class="to-label">To</span>
    <el-input
      ref="inputRef"
      v-model="internalValue"
      :class="showToLabel ? 'to-label-enabled' : ''"
      :placeholder="placeholder"
      type="text"
      maxlength="255"
      size="large"
      autosize
      @blur="updateShowError()"
      @focus="showError = false"
    />
  </div>
</template>
<script lang="ts">
/* General */
import { defineComponent } from 'vue'
import schema from 'async-validator'

/* Mixins */

/* Components */

/* Helpers */

/* Data types */

/* API */

/* This component should be used as a standard email input. It comes with...
   1) Red validation border if the email is not valid, upon blur
   2) Option to validate email if user goes back to blank (after first typing)
   3) Emits:
      * valid - When current value is a valid email
      * invalid - When current value is an invalid email
      * valid-on-blur - When value is a valid email on blur
      * invalid-on-blur - When value is an invalid email on blur
*/
export default defineComponent({
  name: 'StandardEmailInput',

  props: {
    modelValue: {
      type: String,
      default: '',
    },
    placeholder: {
      type: String,
      required: false,
      default: '',
    },
    validateIfBlank: {
      type: Boolean,
      default: false,
    },
    showToLabel: {
      type: Boolean,
      default: false,
    },
    focusField: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['invalid', 'invalid-on-blur', 'update:modelValue', 'valid', 'valid-on-blur'],

  data() {
    return {
      invalid: false,
      showError: false,
    }
  },

  computed: {
    internalValue: {
      get(): string | number {
        return this.modelValue
      },
      set(value: any): any {
        this.$emit('update:modelValue', value)
      },
    },
    showEmailValidationIcon(): boolean {
      return Boolean(this.internalValue)
    },
  },

  watch: {
    focusField(value: boolean) {
      if (value) {
        // @ts-ignore
        this.$refs.inputRef.focus()
      }
    },
    internalValue(value: string) {
      if (value === null) {
        return false
      }

      const validator = new schema({
        email: {
          type: 'email',
          required: this.validateIfBlank,
        },
      })
      validator.validate(
        { email: value.trim() },
        (errors: any, fields: any) => {
          this.invalid = errors ? true : false
          if (!errors) {
            this.$emit('valid')
          } else {
            this.$emit('invalid')
          }
        },
      )
    },
  },

  methods: {
    updateShowError() {
      this.showError = this.invalid
      this.invalid ? this.$emit('invalid-on-blur') : this.$emit('valid-on-blur')
    },
  },
})
</script>
<style lang="scss">
.standard-email-input {
  position: relative;

  .to-label {
    position: absolute;
    top: 13px;
    left: 12px;
    z-index: 2;
    @include lettering--h5;
  }

  .el-input.to-label-enabled input {
    padding-left: 36px;
  }

  &.invalid {
    .el-input__wrapper {
      box-shadow: 0 0 0 2px $color--red inset;
    }
  }
}
</style>
